/**
 * Sign-in: email and password (the authenticator-code step follows), or a
 * passkey (the browser's picker, or autofill on the email field), which
 * needs no code. "Forgot password?" hands the typed email to the reset form.
 */

import { useState, type FormEvent } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { usePasskeySignIn } from '../hooks/usePasskeySignIn';
import {
  AUTH_ERROR_CLASS,
  AUTH_INLINE_LINK_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_SECONDARY_BUTTON_CLASS,
  AUTH_SUBMIT_CLASS,
  AUTH_SUBMIT_STYLE,
} from './authFormStyles';

interface SignInFormProps {
  /** Show the reset form, starting from the email typed so far. */
  onForgotPassword: (email: string) => void;
}

export function SignInForm({ onForgotPassword }: SignInFormProps) {
  const { login } = useAuth();
  const passkey = usePasskeySignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : null) || 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="w-full">
        <div className="mb-4">
          <label htmlFor="email" className={AUTH_LABEL_CLASS}>
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            // "webauthn" lets the browser offer passkeys in this field's autofill.
            autoComplete="username webauthn"
            className={AUTH_INPUT_CLASS}
          />
        </div>

        <div className="mb-6">
          <div className="flex items-baseline justify-between">
            <label htmlFor="password" className={AUTH_LABEL_CLASS}>
              Password
            </label>
            <button
              type="button"
              onClick={() => onForgotPassword(email)}
              className={AUTH_INLINE_LINK_CLASS}
            >
              Forgot password?
            </button>
          </div>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="••••••••"
            autoComplete="current-password"
            className={AUTH_INPUT_CLASS}
          />
        </div>

        {error && (
          <div role="alert" className={AUTH_ERROR_CLASS}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={AUTH_SUBMIT_CLASS}
          style={AUTH_SUBMIT_STYLE}
        >
          {isSubmitting ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      {passkey.supported && (
        <>
          <div className="my-4 flex items-center gap-3 text-xs text-text-muted">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
          <button
            type="button"
            onClick={() => void passkey.signInWithPicker()}
            disabled={passkey.isSigningIn}
            className={AUTH_SECONDARY_BUTTON_CLASS}
          >
            {passkey.isSigningIn ? 'Waiting for your passkey...' : 'Sign in with a passkey'}
          </button>
          {passkey.error && (
            <div role="alert" className={`${AUTH_ERROR_CLASS} mt-4`}>
              {passkey.error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
